/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/report-signatory.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import Loading from '@components/core/loading';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    removePropertiesFromObject,
    getProcessedFieldChangeItems,
    getFieldChangeItems,
} from 'views/pages/admin/audit-log/service';
import Constants, { ReportStatusNames } from '@utils/constants';
import {
    useGetPowerBIModuleTypes,
    useGetPowerBIReportFormatTypes,
    useGetPowerBIReportTemplates,
} from '@apis/bff-api/report-templates';

const propArray = [
    '_id',
    'CurrentUserId',
    'CreatedDate',
    'CreatedByName',
    'CreatedByUserId',
    'UpdatedDate',
    'UpdatedByUserId',
    'UpdatedByName',
    'Organization',
    'OrgKey',
    'RecordId',
    'PbiDatasetId',
    'PbiReportId',
    'PublishReport',
    'InUse',
    'ReportTemplateId',
    'OrgTemplateStatus',
    'LastAttemptedPublish',
    'HasGeneratedReports',
];
const CMArray = ['IncludeComparisonDataset'];
const RAMArray = ['IncludeInternalRisks', 'IncludeNotApplicableRisks', 'IncludeReviews'];

const OpraReportTemplateRecordChangeGridPBI = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    // The creation-time values are not being stored intentionally.
    // For example, the Power BI template is retrieved dynamically from the Power BI API.
    // Storing a static snapshot would make it difficult to track changes to the template over time.
    // This approach was reviewed and agreed upon by the Product team.
    const { data: moduleTypesData, isLoading: isLoadingModuleTypes } = useGetPowerBIModuleTypes();
    const { data: formatTypesData, isLoading: isLoadingFormatTypes } = useGetPowerBIReportFormatTypes();
    const { data: powerBIReportTemplatesData, isLoading: isLoadingSystemPBITemplates } = useGetPowerBIReportTemplates(
        Constants.ObjectId.Empty,
    );
    const ArrayItems = (rec: any) => {
        if (
            rec.Module === Constants.Modules.CM ||
            rec.Module === Constants.Modules.CM2 ||
            rec.Module === Constants.Modules.SM
        ) {
            return propArray.concat(...RAMArray);
        }
        if (rec.Module === Constants.Modules.RAM) {
            return propArray.concat(...CMArray);
        }
        if (!Object.prototype.hasOwnProperty.call(rec, 'IncludeComparisonDataset')) {
            return propArray.concat(...CMArray);
        }
        return propArray.concat(...RAMArray);
    };
    const oldRecordPowerBiTemplateName = oldRecord?.PbiReportId
        ? powerBIReportTemplatesData?.find((rt) => rt.id === oldRecord.PbiReportId)?.name
        : null;
    const refactoredOldRecord = {
        ...(oldRecordPowerBiTemplateName ? { PowerBITemplate: oldRecordPowerBiTemplateName } : {}),
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, ArrayItems(newRecord)),
    };

    const newRecordPowerBiTemplateName = newRecord?.PbiReportId
        ? powerBIReportTemplatesData?.find((rt) => rt.id === newRecord.PbiReportId)?.name
        : null;
    const refactoredNewRecord = {
        ...(newRecordPowerBiTemplateName ? { PowerBITemplate: newRecordPowerBiTemplateName } : {}),
        ...newRecord,
        ...removePropertiesFromObject(newRecord, ArrayItems(newRecord)),
    };

    const enums: Record<string, any[]> = {
        Module: moduleTypesData?.reduce(
            (acc, curr) => {
                acc[curr.id] = curr.name.replace(' ', '_');
                return acc;
            },
            {} as Record<number, string>,
        ),
        Format: ['', ...(formatTypesData?.map((i) => i.name) ?? [])],
        Status: ['', ...Object.values(ReportStatusNames)],
    };
    const lookups: any = {
        Organization: (oldRec: any, newRec: any) => newRec?.Organization?.Name,
    };

    const itemsCustomRenderer = (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            lookups,
        });

    const dateTimeFieldKeys = ['RetirementDate'];

    const items = getFieldChangeItems(
        refactoredOldRecord,
        refactoredNewRecord,
        null,
        itemsCustomRenderer,
        dateTimeFieldKeys,
    );

    if (isLoadingModuleTypes || isLoadingFormatTypes || isLoadingSystemPBITemplates) return <Loading />;

    return <FieldChangeGrid items={items} />;
};

export { OpraReportTemplateRecordChangeGridPBI };
